import axios from 'axios';

export interface AlanchandUSDData {
    usd_cash_buy: number;    // USD Cash Buy Rate (in Toman)
    usd_cash_sell: number;   // USD Cash Sell Rate (in Toman)
    usd_transfer_buy: number; // USD Transfer Buy Rate (in Toman)
    usd_transfer_sell: number; // USD Transfer Sell Rate (in Toman)
    last_updated: string;
    source: 'Alanchand' | 'Alanchand (Mock)';
    raw_html?: string;  // For debugging
}

// Mock data for fallback
const MOCK_ALANCHAND_DATA: AlanchandUSDData = {
    usd_cash_buy: 87000,
    usd_cash_sell: 87000,
    usd_transfer_buy: 87000,
    usd_transfer_sell: 87000,
    last_updated: new Date().toISOString(),
    source: 'Alanchand (Mock)'
};

/**
 * Get USD rates from Alanchand website
 * Based on: https://alanchand.com/en/currencies-price/usd-hav
 */
export const getAlanchandUSDRates = async (): Promise<AlanchandUSDData> => {
    try {
        console.log('🌐 Fetching USD rates from Alanchand...');

        // Use localhost:3001 in development, /api in production
        const apiUrl = import.meta.env.DEV
            ? 'http://localhost:3001/api/alanchand'
            : '/api/alanchand';

        const response = await axios.get(apiUrl, {
            timeout: 15000,
            headers: {
                'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
                'Accept-Language': 'en-US,en;q=0.9'
            }
        });

        const htmlContent = response.data;
        console.log('✅ Successfully fetched from Alanchand');
        console.log('📄 HTML content length:', htmlContent.length);

        // Check if DOMParser is available (browser environment)
        if (typeof DOMParser === 'undefined') {
            console.warn('⚠️ DOMParser not available, using mock data');
            return MOCK_ALANCHAND_DATA;
        }

        // Parse HTML
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlContent, 'text/html');

        // Look for USD rate in the page
        let usdRate = 0;

        // Strategy 1: Look for the main USD rate text
        const text = doc.body.textContent || '';
        const rateMatch = text.match(/1 US dollar is equal to ([\d,]+) Iranian Toman/);
        if (rateMatch) {
            usdRate = parseInt(rateMatch[1].replace(/,/g, ''));
            console.log('✅ Found USD rate from text:', usdRate);
        }

        // Strategy 2: Look for large numbers that could be USD rates (1,000,000+ range)
        if (!usdRate) {
            const largeNumberMatch = text.match(/(\d{1,3}(?:,\d{3}){2,})/);
            if (largeNumberMatch) {
                const candidateRate = parseInt(largeNumberMatch[1].replace(/,/g, ''));
                // Check if it's in a reasonable range for USD to Toman (500,000 - 2,000,000)
                if (candidateRate >= 500000 && candidateRate <= 2000000) {
                    usdRate = candidateRate;
                    console.log('✅ Found USD rate from large number pattern:', usdRate);
                }
            }
        }

        // Strategy 3: Look for table data
        if (!usdRate) {
            const tables = doc.querySelectorAll('table');
            for (const table of tables) {
                const rows = table.querySelectorAll('tr');
                for (const row of rows) {
                    const cells = row.querySelectorAll('td');
                    for (const cell of cells) {
                        const text = cell.textContent?.trim() || '';
                        if (text.includes('USD') || text.includes('Dollar')) {
                            const priceMatch = text.match(/(\d{1,3}(?:,\d{3})*)/);
                            if (priceMatch) {
                                usdRate = parseInt(priceMatch[1].replace(/,/g, ''));
                                console.log('✅ Found USD rate from table:', usdRate);
                                break;
                            }
                        }
                    }
                    if (usdRate) break;
                }
                if (usdRate) break;
            }
        }

        if (!usdRate) {
            console.warn('⚠️ No valid USD rate found in Alanchand HTML');
            throw new Error('Could not extract USD rate from Alanchand website');
        }

        const rateData: AlanchandUSDData = {
            usd_cash_buy: usdRate,
            usd_cash_sell: usdRate,
            usd_transfer_buy: usdRate,
            usd_transfer_sell: usdRate,
            last_updated: new Date().toISOString(),
            source: 'Alanchand'
        };

        console.log('✅ Successfully scraped Alanchand USD rates:', rateData);
        return rateData;

    } catch (error: any) {
        console.error('❌ Error fetching from Alanchand:', error?.message || 'Unknown error');
        throw error; // Re-throw the error instead of falling back to mock data
    }
};

/**
 * Get only the cash sell rate (most commonly used)
 */
export const getAlanchandUSDCashSellRate = async (): Promise<number> => {
    try {
        const rates = await getAlanchandUSDRates();
        return rates.usd_cash_sell;
    } catch (error) {
        console.error('Error getting USD cash sell rate:', error);
        return MOCK_ALANCHAND_DATA.usd_cash_sell;
    }
};

/**
 * Test function to validate the Alanchand scraper
 */
export const testAlanchandService = async (): Promise<void> => {
    console.log('🧪 Testing Alanchand USD Service...');

    try {
        const result = await getAlanchandUSDRates();
        console.log('📊 Test Result:', JSON.stringify(result, null, 2));

        if (result.source === 'Alanchand') {
            console.log('✅ Test PASSED - Successfully scraped real data from Alanchand');
            console.log(`💰 USD Cash Buy: ${result.usd_cash_buy} Toman`);
            console.log(`💰 USD Cash Sell: ${result.usd_cash_sell} Toman`);
            console.log(`💳 USD Transfer Buy: ${result.usd_transfer_buy} Toman`);
            console.log(`💳 USD Transfer Sell: ${result.usd_transfer_sell} Toman`);
        } else {
            console.log('⚠️ Test PARTIAL - Using mock data');
        }

    } catch (error) {
        console.error('❌ Test FAILED:', error);
    }
}; 